"use client";;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:ENCON_SENTRY_KEY":true});}catch(e){}}();
import {useTranslations} from "next-intl";
import {useSearchParams} from "next/navigation";
import type React from "react";
import {Suspense, useCallback, useMemo, useState} from "react";
import {ArchiveFilter} from "@/components/ArchiveFilter";
import {
	parsePageParam,
	saveArchiveFilterQuery,
} from "@/components/ArchiveFilter/useArchiveFilterParams";
import {CollectionArchive} from "@/components/CollectionArchive";
import {usePathname, useRouter} from "@/i18n/routing";
import {Media} from "@/components/Media";
import Slider from "@/components/Slider";
import type {Case, Insight, Media as MediaType, Tag} from "@/payload-types";

type IndustryLogo = {
	id: string;
	industry: string | Tag;
	image: (string | MediaType)[];
	_status?: string | null;
};

export type Props = {
	solutionTags: Tag[];
	industryTags: Tag[];
	frameworkTags: Tag[];
	industryLogos: IndustryLogo[];
	initialInsights: Insight[];
	initialCases: Case[];
	relationTo: any;
	introContent?: any;
	enableLink?: any;
	link?: any;
	view?: "grid" | "slider" | "archive" | undefined;
	blockId?: string; // Add blockId prop
};

export const ArchiveFilterWrapper: React.FC<Props> = ({
	solutionTags,
	industryTags,
	frameworkTags,
	industryLogos,
	initialInsights,
	initialCases,
	relationTo,
	introContent,
	enableLink,
	link,
	view,
	blockId,
}) => {
	const pathname = usePathname();
	const router = useRouter();
	const searchParams = useSearchParams();
	const currentSlug = pathname.split("/").pop() || "";

	// Exclude the current page from lists to avoid showing it in "related" blocks
	const filteredInitialCases = useMemo(
		() => initialCases.filter((item) => item.slug !== currentSlug),
		[initialCases, currentSlug],
	);
	const filteredInitialInsights = useMemo(
		() => initialInsights.filter((item) => item.slug !== currentSlug),
		[initialInsights, currentSlug],
	);

	const [filteredCases, setFilteredCases] = useState(filteredInitialCases);
	const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
	const [filteredInsights, setFilteredInsights] = useState(filteredInitialInsights);
	const [currentPage, setCurrentPage] = useState(() =>
		parsePageParam(searchParams.get("page")),
	);
	const [hasActiveFilters, setHasActiveFilters] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const t = useTranslations("Archive");

	// Filter logos based on selected industries and transform to HighlightsBlock format
	const filterLogos = useMemo(() => {
		if (!selectedIndustries.length || !industryLogos?.length) {
			return [];
		}

		const filteredLogos = industryLogos.filter((logo) => {
			const industryId =
				typeof logo.industry === "string" ? logo.industry : logo.industry?.id;
			return industryId && selectedIndustries.includes(industryId);
		});

		// Transform industryLogos to HighlightsBlock brands format
		// Each logo.image is an array, so we flatten them into individual brand items
		return filteredLogos.flatMap((logo) => {
			if (!logo.image || !Array.isArray(logo.image)) {
				return [];
			}
			return logo.image.map((img, index) => ({
				id: `${logo.id}-${index}`,
				image: img,
			}));
		});
	}, [selectedIndustries, industryLogos]);

	// Memoize filter function to prevent unnecessary recalculations
	const filterItems = useCallback(
		(items: (Case | Insight)[], filters: Record<string, string[]>) => {
			if (
				Object.keys(filters).length === 0 ||
				Object.values(filters).every((arr) => arr.length === 0)
			) {
				return items;
			}

			return items.filter((item) => {
				// Get all tag IDs from the item (handling different tag field names)
				const itemTagIds: string[] = [];

				// Handle cases with separate tag fields
				if ("tagsSolutions" in item && item.tagsSolutions) {
					itemTagIds.push(
						...item.tagsSolutions.map((tag) =>
							typeof tag === "string" ? tag : tag.id,
						),
					);
				}
				if ("tagsIndustries" in item && item.tagsIndustries) {
					itemTagIds.push(
						...item.tagsIndustries.map((tag) =>
							typeof tag === "string" ? tag : tag.id,
						),
					);
				}
				if ("tagsFramework" in item && item.tagsFramework) {
					itemTagIds.push(
						...item.tagsFramework.map((tag) =>
							typeof tag === "string" ? tag : tag.id,
						),
					);
				}

				// Handle insights with single tags array (if they exist)
				if ("tags" in item && item.tags && Array.isArray(item.tags)) {
					itemTagIds.push(
						...item.tags.map((tag) => (typeof tag === "string" ? tag : tag.id)),
					);
				}

				if (itemTagIds.length === 0) {
					return false;
				}

				// Check if item matches any selected filters
				for (const [category, selectedTagIds] of Object.entries(filters)) {
					if (selectedTagIds.length > 0) {
						const hasMatchingTag = selectedTagIds.some((tagId) =>
							itemTagIds.includes(tagId),
						);
						if (!hasMatchingTag) return false;
					}
				}

				return true;
			});
		},
		[],
	);

	// Memoize search function
	const searchItems = useCallback((items: any[], query: string) => {
		if (!query.trim()) return items;

		const lowerQuery = query.toLowerCase();
		return items.filter((item) => {
			const title = item.title?.toLowerCase() || "";
			const description = item.meta?.description?.toLowerCase() || "";
			return title.includes(lowerQuery) || description.includes(lowerQuery);
		});
	}, []);

	const scrollToBlock = useCallback(() => {
		if (blockId) {
			// Use requestAnimationFrame for smoother scrolling
			requestAnimationFrame(() => {
				setTimeout(() => {
					const blockElement = document.getElementById(`block-${blockId}`);
					if (blockElement) {
						const headerOffset = 150;
						const elementPosition = blockElement.getBoundingClientRect().top;
						const offsetPosition =
							elementPosition + window.pageYOffset - headerOffset;

						window.scrollTo({
							top: offsetPosition,
							behavior: "smooth",
						});
					}
				}, 50); // Reduced timeout for faster response
			});
		}
	}, [blockId]);

	// Memoize filtered results
	const { finalCases, finalInsights } = useMemo(() => {
		// This will only recalculate when dependencies change
		return {
			finalCases: filteredCases,
			finalInsights: filteredInsights,
		};
	}, [filteredCases, filteredInsights]);

	const handleFilterChange = useCallback(
		(
			filters: Record<string, string[]>,
			search?: string,
			options?: {skipScroll?: boolean},
		) => {
			const hasFilters = Object.values(filters).some((arr) => arr.length > 0);
			const hasSearch = search && search.trim().length > 0;

			setHasActiveFilters(hasFilters || !!hasSearch);
			setSearchQuery(search || "");

			// First apply tag filters
			let newFilteredCases = filterItems(filteredInitialCases, filters) as Case[];
			let newFilteredInsights = filterItems(
				filteredInitialInsights,
				filters,
			) as Insight[];

			// Then apply search filter
			if (hasSearch) {
				newFilteredCases = searchItems(newFilteredCases, search || "");
				newFilteredInsights = searchItems(newFilteredInsights, search || "");
			}

			setFilteredCases(newFilteredCases);
			setFilteredInsights(newFilteredInsights);
			if (!options?.skipScroll) {
				setCurrentPage(1);
			}

			if (options?.skipScroll) return;

			// Scroll to block when searching or filtering
			// Only scroll if not typing (search will be debounced in ArchiveFilter)
			const isTyping = searchQuery !== search;
			if (!isTyping) {
				scrollToBlock();
			}
		},
		[
			filteredInitialCases,
			filteredInitialInsights,
			searchQuery,
			scrollToBlock,
			filterItems,
			searchItems,
		],
	);

	const onPageChange = useCallback(
		(page: number) => {
			setCurrentPage(page);
			scrollToBlock();

			const params = new URLSearchParams(searchParams.toString());
			if (page <= 1) {
				params.delete("page");
			} else {
				params.set("page", String(page));
			}
			const query = params.toString();
			saveArchiveFilterQuery(pathname, query);

			if (query === searchParams.toString()) return;
			router.replace(query ? `${pathname}?${query}` : pathname, {
				scroll: false,
			});
		},
		[pathname, router, searchParams, scrollToBlock],
	);

	const hasNoResults =
		hasActiveFilters &&
		filteredCases.length === 0 &&
		filteredInsights.length === 0;

	return (
		<>
			<div
				className={`archive_filters${filterLogos.length > 0 ? "has_logos" : ""}`}
			>
				<Suspense>
					<ArchiveFilter
						solutionTags={solutionTags}
						industryTags={industryTags}
						frameworkTags={frameworkTags}
						onFilterChange={handleFilterChange}
						onIndustriesChange={setSelectedIndustries}
					/>
				</Suspense>
			</div>

			{relationTo === "cases" && filterLogos && filterLogos.length > 0 && (
				<div className="archive_slider highlights_block">
					<Slider
						slides={filterLogos.map((logo) => (
							<div key={logo.id}>
								{logo.image && <Media resource={logo.image} />}
							</div>
						))}
						slidesPerView={5}
						grid={{
							rows: 2,
							fill: "row",
						}}
						spaceBetween={32}
						autoplay={{
							delay: 2000,
							disableOnInteraction: false,
						}}
						breakpoints={{
							320: { slidesPerView: 2.5, spaceBetween: 24 },
							768: { slidesPerView: 5 },
						}}
					/>
				</div>
			)}

			{hasNoResults ? (
				<div className="archive_no-results">
					<p>{t("noResults")}</p>
				</div>
			) : (
				<CollectionArchive
					richText={introContent}
					enableLink={enableLink}
					link={link}
					insights={finalInsights}
					cases={finalCases}
					view={view}
					blockId={blockId}
					page={currentPage}
					onPageChange={onPageChange}
				/>
			)}
		</>
	);
};
